import React, { useState, useCallback } from 'react';
import { SellerLayout } from '../SellerLayout';
import {
  PageHeading,
  CardBalance,
  Pills,
  Button,
  Text,
  Heading,
  Tooltip,
  Drawer,
  Input,
  Alert,
  Loading,
  Icon,
  Avatar,
  Section,
} from 'barte-design-system';
import {
  ArrowRightIcon,
  CheckIcon,
  LockIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import './DashboardSaldosV3.css';

// ─── Tooltip cast (children omitido no tipo público) ─────────────────────────

const TooltipAny = Tooltip as React.ForwardRefExoticComponent<any>;

// ─── Ícones dos cards (chevron e lock) ───────────────────────────────────────

const ChevronIcon: React.ElementType = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <Icon icon={CaretRightIcon} size={16} />
  </span>
);

const BolsaoLockIcon: React.ElementType = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <Icon icon={LockIcon} size={16} />
  </span>
);


// ─── Conteúdo dos drawers de info (substitui os tooltips dos cards) ───────────

type InfoCardKey = 'processamento' | 'futuros';

type PillStateLite = 'accent' | 'warning' | 'success' | 'info' | 'default';

type InfoRow = {
  label: string;
  value: string;
  pillState: PillStateLite;
};

type Bullet = { term: string; description: string };

type CardInfo = {
  title: string;
  intro: string;
  bullets?: Bullet[];
  rows: InfoRow[];
};

const CARD_INFO: Record<InfoCardKey, CardInfo> = {
  processamento: {
    title: 'Saque em processamento',
    intro: 'Valores a caminho direto da sua conta bancária — não passam pelo Disponível.',
    bullets: [
      { term: 'Liquida hoje', description: 'saques que você solicitou (PIX, boleto, antecipações).' },
      { term: 'Liquida até próximo dia útil', description: 'vendas em cartão na fila de liquidação automática.' },
    ],
    rows: [
      { label: 'Liquida hoje', value: 'R$ 170.000,00', pillState: 'warning' },
      { label: 'Liquida até próximo dia útil', value: 'R$ 180.000,00', pillState: 'warning' },
    ],
  },
  futuros: {
    title: 'Lançamentos futuros',
    intro: 'Vendas com data prevista de liberação que ainda não entraram na fila.',
    bullets: [
      { term: 'A liberar', description: 'pode ser antecipado para sacar antes do prazo natural.' },
      { term: 'Gravame', description: 'recebíveis dados como garantia — não pode ser antecipado.' },
    ],
    rows: [
      { label: 'A liberar', value: 'R$ 120.000,00', pillState: 'accent' },
      { label: 'Gravame', value: 'R$ 30.000,00', pillState: 'info' },
    ],
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

type SaqueStep = 'empty' | 'filled' | 'processing' | 'confirmation';
type PeriodKey = 'week' | 'month' | 'jan';

// ─── Mock data ────────────────────────────────────────────────────────────────

const SALDO_TOTAL = 540000;
const DISPONIVEL = 40000;
const PROCESSAMENTO = 350000;
const FUTUROS = 150000;
const FUTURO_A_LIBERAR = 120000;

const CHART_DATA = [
  { label: '01', received: 0, overdue: 0 },
  { label: '03', received: 0, overdue: 0 },
  { label: '05', received: 0, overdue: 0 },
  { label: '07', received: 0, overdue: 0 },
  { label: '08', received: 45, overdue: 40, isToday: true },
  { label: '11', received: 0, overdue: 0 },
  { label: '13', received: 0, overdue: 0 },
  { label: '15', received: 0, overdue: 0 },
  { label: '17', received: 0, overdue: 0 },
  { label: '18', received: 0, overdue: 0 },
  { label: '21', received: 0, overdue: 0 },
  { label: '23', received: 0, overdue: 0 },
  { label: '25', received: 0, overdue: 0 },
  { label: '27', received: 0, overdue: 0 },
  { label: '28', received: 0, overdue: 0 },
  { label: '31', received: 0, overdue: 0 },
];

const MAX_CHART_VALUE = Math.max(...CHART_DATA.map((d) => d.received + d.overdue), 1);

const RECEBIVEIS_MOCK = [
  {
    id: '1',
    initials: 'VG',
    name: 'Vinicius Gonçalves...',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1,00',
    status: 'Em atraso' as const,
    statusState: 'error' as const,
  },
  {
    id: '2',
    initials: 'VG',
    name: 'Vinicius Gonçalves...',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1,00',
    status: 'Paga - 06/01' as const,
    statusState: 'success' as const,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function DashboardSaldosV3() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saqueStep, setSaqueStep] = useState<SaqueStep>('empty');
  const [saqueValue, setSaqueValue] = useState('');
  const [valorRepasse, setValorRepasse] = useState(0);
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('month');
  const [infoCard, setInfoCard] = useState<InfoCardKey | null>(null);

  const openInfoDrawer = useCallback((card: InfoCardKey) => setInfoCard(card), []);
  const closeInfoDrawer = useCallback(() => setInfoCard(null), []);

  // ── Drawer handlers ──────────────────────────────────────────────────────

  const handleOpenDrawer = useCallback(() => {
    setSaqueStep('empty');
    setSaqueValue('');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => {
      setSaqueStep('empty');
      setSaqueValue('');
    }, 300);
  }, []);

  const DISPONIVEL_TOTAL = DISPONIVEL + FUTURO_A_LIBERAR;

  const formatCurrency = (raw: string) => {
    const digits = raw.replaceAll(/\D/g, '');
    if (!digits) return '';
    const num = Number.parseInt(digits, 10) / 100;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseValue = (formatted: string) => {
    if (!formatted) return 0;
    return Number.parseFloat(formatted.replaceAll('.', '').replace(',', '.')) || 0;
  };

  const calcSaque = (formatted: string) => {
    const val = parseValue(formatted);
    const excede = val > DISPONIVEL_TOTAL;
    const taxa = val > DISPONIVEL ? (val - DISPONIVEL) * 0.05 : 0;
    const recebe = val - taxa;
    return { val, excede, taxa, recebe };
  };

  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const formatted = formatCurrency(e.target.value);
      setSaqueValue(formatted);
      setSaqueStep(formatted ? 'filled' : 'empty');
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    const val = parseValue(saqueValue);
    const taxa = val > DISPONIVEL ? (val - DISPONIVEL) * 0.05 : 0;
    setValorRepasse(val - taxa);
    setSaqueStep('processing');
    setTimeout(() => setSaqueStep('confirmation'), 2000);
  }, [saqueValue]);

  // ── Drawer footer ─────────────────────────────────────────────────────────

  function getDrawerFooter() {
    if (saqueStep === 'confirmation') {
      return {
        primaryButton: { children: 'Entendi', onClick: handleCloseDrawer },
        secondaryButton: { children: 'Extrato de saques', onClick: handleCloseDrawer },
      };
    }

    return {
      primaryButton: {
        children: 'Solicitar saque',
        iconRight: <Icon icon={ArrowRightIcon} size={16} />,
        onClick: handleConfirm,
        disabled:
          saqueStep === 'empty' ||
          saqueStep === 'processing' ||
          calcSaque(saqueValue).excede,
      },
      secondaryButton: {
        children: 'Cancelar',
        onClick: handleCloseDrawer,
        disabled: saqueStep === 'processing',
      },
    };
  }

  // ── Drawer content ────────────────────────────────────────────────────────

  function renderDrawerContent() {
    if (saqueStep === 'processing') {
      return (
        <div className="drawer-loading">
          <Loading type="page" description="Carregando solicitação" />
        </div>
      );
    }

    if (saqueStep === 'confirmation') {
      return (
        <div className="drawer-confirmation">
          <div className="drawer-success-icon">
            <Icon icon={CheckIcon} size={32} color="white" weight="bold" />
          </div>
          <div className="drawer-confirmation-header">
            <Text variant="h4" style={{ textAlign: 'center' }}>
              Solicitação efetuada com sucesso!
            </Text>
            <Text
              variant="body-sm"
              style={{ textAlign: 'center', color: 'var(--content-secondary)' }}
            >
              O valor vai aparecer em "Saque em processamento" e cair direto na sua conta
              bancária na próxima janela de liquidação.
            </Text>
          </div>
          <div className="drawer-confirmation-section">
            <Text variant="label-lg">Informações do saque</Text>
            <div className="drawer-summary">
              <div className="drawer-summary-row">
                <Text variant="body-sm">Valor solicitado</Text>
                <Text variant="label-sm">
                  R${' '}
                  {valorRepasse.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </div>
              <div className="drawer-summary-divider" />
              <div className="drawer-summary-row">
                <Text variant="body-sm">De</Text>
                <Text variant="label-sm">Disponível para saque</Text>
              </div>
              <div className="drawer-summary-divider" />
              <div className="drawer-summary-row">
                <Text variant="body-sm">Para</Text>
                <Text variant="label-sm">Conta corrente</Text>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isFilled = saqueStep === 'filled';
    const { excede, taxa, recebe } = calcSaque(saqueValue);

    const fmt = (n: number) =>
      n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
      <div className="drawer-saque">
        <Text variant="h4">
          {isFilled ? 'Confirme o valor da solicitação' : 'Qual o valor do saque?'}
        </Text>

        {/* Breakdown */}
        <div className="drawer-breakdown">
          <div className="drawer-breakdown-bar">
            <div
              className="drawer-bar-segment"
              style={{ flex: DISPONIVEL, background: 'var(--accent-green)' }}
            />
            <div
              className="drawer-bar-segment"
              style={{ flex: FUTURO_A_LIBERAR, background: 'var(--accent-purple)' }}
            />
          </div>
          <div
            className={`drawer-breakdown-rows${
              isFilled ? ' drawer-breakdown-rows--dimmed' : ''
            }`}
          >
            <div className="drawer-breakdown-row">
              <div className="drawer-breakdown-label">
                <span
                  className="drawer-dot"
                  style={{ background: 'var(--accent-green)' }}
                />
                <Text variant="body-sm">Disponível para saque</Text>
              </div>
              <Text variant="body-sm">R$ {fmt(DISPONIVEL)}</Text>
            </div>
            <div className="drawer-breakdown-row">
              <div className="drawer-breakdown-label">
                <span
                  className="drawer-dot"
                  style={{ background: 'var(--accent-purple)' }}
                />
                <Text variant="body-sm">Disponível para antecipação</Text>
              </div>
              <Text variant="body-sm">R$ {fmt(FUTURO_A_LIBERAR)}</Text>
            </div>
            <div className="drawer-breakdown-row">
              <Text variant="body-sm" style={{ fontWeight: 600 }}>
                Total disponível
              </Text>
              <Text variant="body-sm" style={{ fontWeight: 600 }}>
                R$ {fmt(DISPONIVEL_TOTAL)}
              </Text>
            </div>
          </div>
        </div>

        {/* Input */}
        <Input
          placeholder="R$ 0,00"
          value={saqueValue ? `R$ ${saqueValue}` : ''}
          onChange={handleValueChange}
          error={excede ? 'Valor superior ao disponível para saque.' : undefined}
        />

        {/* Summary (estado preenchido e sem erro) */}
        {isFilled && !excede && (
          <div className="drawer-summary">
            <div className="drawer-summary-row">
              <Text variant="body-sm">Total solicitado</Text>
              <Text variant="body-sm">R$ {saqueValue}</Text>
            </div>
            <div className="drawer-summary-divider" />
            <div className="drawer-summary-row">
              <Text variant="body-sm">Antecipando</Text>
              <Text variant="body-sm">
                R$ {fmt(Math.max(0, calcSaque(saqueValue).val - DISPONIVEL))}
              </Text>
            </div>
            <div className="drawer-summary-divider" />
            <div className="drawer-summary-row">
              <Text variant="body-sm">Taxa de antecipação</Text>
              <Text variant="body-sm">R$ {fmt(taxa)}</Text>
            </div>
            <div className="drawer-summary-divider" />
            <div className="drawer-summary-row">
              <Text variant="label-sm">Você recebe</Text>
              <Text variant="label-sm">R$ {fmt(recebe)}</Text>
            </div>
          </div>
        )}

        {/* Alert */}
        <Alert
          variant="info"
          title=""
          description="O valor solicitado vai aparecer em 'Saque em processamento' e cai direto na sua conta bancária na próxima janela de liquidação."
          showCloseButton={false}
        />
      </div>
    );
  }

  // ── Helpers para cards de saldo ──────────────────────────────────────────

  const formatBRL = (n: number) =>
    n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <SellerLayout activeItem="v3">
      <main className="content">
          <div className="content-inner">
            <PageHeading title="Olá, Madu" />

            <div className="dashboard-v3">
              {/* ── Top: hero (saldo total) + banking (mesma altura) ────── */}
              <div className="dashboard-top">
                <div className="hero-card">
                  <div className="hero-stat">
                    <Text variant="body-xs" style={{ color: 'var(--content-tertiary)' }}>
                      Saldo total
                    </Text>
                    <div className="hero-stat-bottom">
                      <div className="hero-bar hero-bar--4">
                        <TooltipAny
                          title="Liberado para saque"
                          label={`R$ ${formatBRL(DISPONIVEL)}`}
                          placement="top"
                          style={{ flex: DISPONIVEL, display: 'flex' }}
                        >
                          <div
                            className="hero-bar-segment"
                            style={{ background: 'var(--accent-green)' }}
                          />
                        </TooltipAny>
                        <TooltipAny
                          title="Até próximo dia útil"
                          label={`R$ ${formatBRL(PROCESSAMENTO)}`}
                          placement="top"
                          style={{ flex: PROCESSAMENTO, display: 'flex' }}
                        >
                          <div
                            className="hero-bar-segment"
                            style={{ background: 'var(--accent-orange)' }}
                          />
                        </TooltipAny>
                        <TooltipAny
                          title="A liberar + Gravames"
                          label={`R$ ${formatBRL(FUTUROS)}`}
                          placement="top"
                          style={{ flex: FUTUROS, display: 'flex' }}
                        >
                          <div
                            className="hero-bar-segment"
                            style={{ background: 'var(--accent-purple)' }}
                          />
                        </TooltipAny>
                        <TooltipAny
                          title="Garantia operacional"
                          label="R$ 2.450,00"
                          placement="top"
                          style={{ flex: 2450, display: 'flex' }}
                        >
                          <div
                            className="hero-bar-segment"
                            style={{ background: 'var(--accent-blue)' }}
                          />
                        </TooltipAny>
                      </div>
                      <div className="hero-stat-value">
                        <Heading variant="h6" style={{ color: 'var(--content-tertiary)' }}>
                          R$
                        </Heading>
                        <Heading variant="h6">{formatBRL(SALDO_TOTAL)}</Heading>
                      </div>
                    </div>
                  </div>

                  <div className="hero-divider" />

                  <div className="hero-stat">
                    <div className="hero-stat-header">
                      <Text variant="body-xs" style={{ color: 'var(--content-tertiary)' }}>
                        Disponível para saque
                      </Text>
                      <Button
                        variant="fill"
                        size="sm"
                        round="full"
                        onClick={handleOpenDrawer}
                      >
                        Solicitar saque
                      </Button>
                    </div>
                    <div className="hero-stat-bottom">
                      <div className="hero-bar hero-bar--2">
                        <TooltipAny
                          title="Liberado para saque"
                          label={`R$ ${formatBRL(DISPONIVEL)}`}
                          placement="top"
                          style={{ flex: DISPONIVEL, display: 'flex' }}
                        >
                          <div
                            className="hero-bar-segment"
                            style={{ background: 'var(--accent-green)' }}
                          />
                        </TooltipAny>
                        <TooltipAny
                          title="A liberar (pode antecipar)"
                          label={`R$ ${formatBRL(FUTURO_A_LIBERAR)}`}
                          placement="top"
                          style={{ flex: FUTURO_A_LIBERAR, display: 'flex' }}
                        >
                          <div
                            className="hero-bar-segment"
                            style={{ background: 'var(--accent-purple)' }}
                          />
                        </TooltipAny>
                      </div>
                      <div className="hero-stat-value">
                        <Heading variant="h6" style={{ color: 'var(--content-tertiary)' }}>
                          R$
                        </Heading>
                        <Heading variant="h6">{formatBRL(DISPONIVEL + FUTURO_A_LIBERAR)}</Heading>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Banking — mesma altura que o hero */}
              <div className="dashboard-banking">
                <div className="banking-extrato-btn">
                  <Icon icon={CaretRightIcon} size={16} weight="bold" color="white" />
                </div>
                <CardBalance color="black">
                  <CardBalance.Info
                    variant="big"
                    title="Conta corrente"
                    balance="450.502,05"
                    showIcon={false}
                    showPills
                    pills={
                      <Pills
                        label="Rendendo 98% CDI"
                        state="success"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>
            </div>

            {/* ── 4 cards de saldo (linha full-width) ─────────────────── */}
            <div className="saldo-cards-row">
              <div className="saldo-card-wrapper">
                <CardBalance className="saldo-card saldo-card--disponivel">
                  <CardBalance.Info
                    variant="big"
                    title="Disponível hoje"
                    balance={formatBRL(DISPONIVEL)}
                    showIcon={false}
                    showPills
                    pills={
                      <Pills
                        label="Liberado para saque"
                        state="success"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>

              <div
                className="saldo-card-clickable"
                onClick={() => openInfoDrawer('processamento')}
                role="button"
                tabIndex={0}
              >
                <CardBalance className="saldo-card saldo-card--processamento">
                  <CardBalance.Info
                    variant="big"
                    title="Saque em processamento"
                    balance={formatBRL(PROCESSAMENTO)}
                    icon={ChevronIcon}
                    showPills
                    pills={
                      <Pills
                        label="Até próximo dia útil"
                        state="warning"
                        variant="light"
                        size="sm"
                      />
                    }
                    bottomSlot={
                      <div className="card-sublist">
                        <div className="sublist-row">
                          <Text variant="body-xs" style={{ color: 'var(--content-secondary)' }}>
                            Liquida hoje
                          </Text>
                          <Text variant="body-xs" style={{ fontWeight: 600 }}>
                            R$ 170.000,00
                          </Text>
                        </div>
                        <div className="sublist-row">
                          <Text variant="body-xs" style={{ color: 'var(--content-secondary)' }}>
                            Liquida até próximo dia útil
                          </Text>
                          <Text variant="body-xs" style={{ fontWeight: 600 }}>
                            R$ 180.000,00
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </CardBalance>
              </div>

              <div
                className="saldo-card-clickable"
                onClick={() => openInfoDrawer('futuros')}
                role="button"
                tabIndex={0}
              >
                <CardBalance className="saldo-card saldo-card--futuros">
                  <CardBalance.Info
                    variant="big"
                    title="Lançamentos futuros"
                    balance={formatBRL(FUTUROS)}
                    icon={ChevronIcon}
                    showPills
                    pills={
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Pills
                          label="A liberar"
                          state="accent"
                          variant="light"
                          size="sm"
                        />
                        <Pills
                          label="Gravames"
                          state="info"
                          variant="light"
                          size="sm"
                        />
                      </div>
                    }
                    bottomSlot={
                      <div className="card-sublist">
                        <div className="sublist-row">
                          <Text variant="body-xs" style={{ color: 'var(--content-secondary)' }}>
                            A liberar
                          </Text>
                          <Text variant="body-xs" style={{ fontWeight: 600 }}>
                            R$ 120.000,00
                          </Text>
                        </div>
                        <div className="sublist-row">
                          <Text variant="body-xs" style={{ color: 'var(--content-secondary)' }}>
                            Gravame
                          </Text>
                          <Text variant="body-xs" style={{ fontWeight: 600 }}>
                            R$ 30.000,00
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </CardBalance>
              </div>

              {/* 4º card: Bolsão (não-clicável) */}
              <div className="saldo-card-wrapper">
                <CardBalance className="saldo-card saldo-card--bolsao">
                  <CardBalance.Info
                    variant="big"
                    title="Bolsão"
                    balance="2.450,00"
                    icon={BolsaoLockIcon}
                    showPills
                    pills={
                      <Pills
                        label="Garantia operacional"
                        state="default"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>
            </div>
            </div>

            {/* ── Seção Recebíveis ───────────────────────────────────── */}
            <div className="receivables-section">
              <div className="receivables-columns">

                {/* ── Coluna esquerda: gráfico ─────────────────────── */}
                <div className="receivables-chart-col">
                  <div className="receivables-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <div className="period-buttons">
                      <Button
                        variant={activePeriod === 'week' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('week')}
                      >
                        Semana
                      </Button>
                      <Button
                        variant={activePeriod === 'month' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('month')}
                      >
                        Mês
                      </Button>
                      <Button
                        variant={activePeriod === 'jan' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('jan')}
                      >
                        janeiro/2026
                      </Button>
                    </div>
                  </div>

                  <div className="chart-container">
                    <div className="chart-bars">
                      {CHART_DATA.map((d) => {
                        const receivedPct = (d.received / MAX_CHART_VALUE) * 100;
                        const overduePct = (d.overdue / MAX_CHART_VALUE) * 100;
                        return (
                          <div key={d.label} className="chart-col">
                            <div className="bar-group">
                              <div
                                className="bar bar-received"
                                style={{ height: `${receivedPct}%` }}
                              />
                              <div
                                className="bar bar-overdue"
                                style={{ height: `${overduePct}%` }}
                              />
                            </div>
                            <Text
                              variant="caption-sm"
                              style={{
                                color: d.isToday
                                  ? 'var(--content-primary)'
                                  : 'var(--content-tertiary)',
                                textAlign: 'center',
                                fontWeight: d.isToday ? 600 : undefined,
                              }}
                            >
                              {d.isToday ? 'hoje' : d.label}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-dot legend-received" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Recebidos
                      </Text>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-overdue" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Em atraso
                      </Text>
                    </div>
                  </div>
                </div>

                {/* ── Coluna direita: lista de recebíveis ──────────── */}
                <div className="receivables-list-col">
                  <div className="receivables-list-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <Button variant="link" size="sm">ver todos</Button>
                  </div>
                  <div className="receivables-search-placeholder" />
                  <div className="receivables-list">
                    {RECEBIVEIS_MOCK.map((item) => (
                      <div key={item.id} className="receivable-item">
                        <Avatar variant="text" size="md" initials={item.initials} />
                        <div className="receivable-item-info">
                          <Text variant="label-sm">{item.name}</Text>
                          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
                            {item.dueLabel}
                          </Text>
                        </div>
                        <div className="receivable-item-right">
                          <Text variant="label-sm">{item.amount}</Text>
                          <Text
                            variant="caption-sm"
                            style={{
                              color:
                                item.statusState === 'error'
                                  ? 'var(--accent-red)'
                                  : 'var(--accent-green)',
                            }}
                          >
                            {item.status}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </main>
    </SellerLayout>

    {/* ── Drawer: Solicitar saque ───────────────────────────────────────── */}
    <Drawer
      isOpen={drawerOpen}
      title="Solicitar saque"
      onClose={handleCloseDrawer}
      footer={getDrawerFooter()}
    >
      {renderDrawerContent()}
    </Drawer>

    {/* ── Drawer: Info de cada card (substitui os tooltips) ─────────────── */}
    <Drawer
      isOpen={infoCard !== null}
      title={infoCard ? CARD_INFO[infoCard].title : ''}
      onClose={closeInfoDrawer}
      footer={{
        primaryButton: { children: 'Entendi', onClick: closeInfoDrawer },
      }}
    >
      {infoCard && (
        <div className="drawer-info-content">
          <Text variant="body-base" style={{ color: 'var(--content-secondary)' }}>
            {CARD_INFO[infoCard].intro}
          </Text>
          {CARD_INFO[infoCard].bullets && (
            <ul className="drawer-info-bullets">
              {CARD_INFO[infoCard].bullets!.map((b, i) => (
                <li key={i}>
                  <Text variant="body-base" style={{ color: 'var(--content-secondary)' }}>
                    <strong style={{ color: 'var(--content-primary)', fontWeight: 600 }}>
                      {b.term}:
                    </strong>{' '}
                    {b.description}
                  </Text>
                </li>
              ))}
            </ul>
          )}
          <Section title="">
            {CARD_INFO[infoCard].rows.map((row, i) => (
              <Section.Row
                key={i}
                label={row.label}
                content={
                  <Pills
                    label={row.value}
                    state={row.pillState}
                    variant="light"
                    size="sm"
                  />
                }
              />
            ))}
          </Section>
        </div>
      )}
    </Drawer>
    </>
  );
}
